// Wacom STU-430 WebHID driver
// Based on pabloko/Wacom-STU-WebHID (STU-540), adapted from a capture of an actual STU-430
// device. Tuned values are documented inline.

const WACOM_VID = 0x056A
const STU430_PID = 0x00A4 // confirmed from real STU-430 capture

const CMD = {
  penData: 0x01,
  information: 0x08,
  capability: 0x09,
  writingMode: 0x0e,
  eSerial: 0x0f,
  clearScreen: 0x20,
  inkMode: 0x21,
  writeImageStart: 0x25,
  writeImageData: 0x26,
  writeImageEnd: 0x27,
  writingArea: 0x2a,
  brightness: 0x2b,
  backgroundColor: 0x2e,
  penColorAndWidth: 0x2d,
  penDataTiming: 0x34,
}

export interface PenPacket {
  /** raw X in tablet units */
  xRaw: number
  /** raw Y in tablet units */
  yRaw: number
  /** raw pressure (0 = no contact, > 0 = touching) */
  pressureRaw: number
  /** pressure normalised to 0..1 */
  pressure: number
  /** convenience: true if the pen is touching the surface */
  drawing: boolean
}

export interface WacomCapability {
  tabletWidth: number
  tabletHeight: number
  pressureFactor: number
  lcdWidth: number
  lcdHeight: number
  refreshRate: number
}

export interface WacomInfo {
  deviceName: string
  firmware: string
  eSerial: string
}

type PenListener = (p: PenPacket) => void
type ConnectionListener = (state: 'connect' | 'disconnect' | 'connecting') => void

/** True when the browser exposes the WebHID API. */
export function isWebHidSupported(): boolean {
  return typeof navigator !== 'undefined' && !!(navigator as any).hid
}

export class WacomStu430 {
  private device: HIDDevice | null = null
  private penListeners = new Set<PenListener>()
  private connListeners = new Set<ConnectionListener>()
  private inputHandler = (e: HIDInputReportEvent) => this.onInputReport(e)

  // Cached after connect.
  capability: WacomCapability | null = null
  info: WacomInfo | null = null

  // Adaptive coordinate scaling: even if `capability` read fails we can still
  // render correctly by tracking the largest values we have actually seen.
  private maxX = 0
  private maxY = 0

  static isSupported = isWebHidSupported

  constructor() {
    // Listen for HID connect/disconnect at the navigator level. These fire
    // when the user physically plugs/unplugs the pad, AND when another tab
    // on the same origin releases its handle — letting us auto-reacquire
    // without forcing the user to click "connect" again.
    if (typeof navigator !== 'undefined' && navigator.hid) {
      navigator.hid.addEventListener('disconnect', this.onHidDisconnect)
      navigator.hid.addEventListener('connect', this.onHidConnect)
    }
  }

  private onHidDisconnect = (e: HIDConnectionEvent) => {
    if (e.device.vendorId !== WACOM_VID || e.device.productId !== STU430_PID) return
    // The HID handle has been invalidated by the browser; drop our reference
    // and notify subscribers so they can flip back to a "not connected" UI.
    this.device = null
    this.capability = null
    this.info = null
    this.maxX = 0
    this.maxY = 0
    this.emitConn('disconnect')
  }

  private onHidConnect = async (e: HIDConnectionEvent) => {
    if (e.device.vendorId !== WACOM_VID || e.device.productId !== STU430_PID) return
    if (this.isConnected()) return
    // The pad just became available (plugged in, or another tab closed and
    // released its handle). Emit a transient 'connecting' so the global
    // toast can show feedback to the user, then try to silently reacquire.
    this.emitConn('connecting')
    try {
      await this.open(e.device)
    } catch {
      // Best-effort: if another tab grabs it first we'll see the next event.
      this.emitConn('disconnect')
    }
  }

  /** Returns a previously-permitted STU-430 if one is plugged in, else null. */
  async findAlreadyGranted(): Promise<HIDDevice | null> {
    if (!isWebHidSupported()) return null
    const devs = await navigator.hid!.getDevices()
    return (
      devs.find(
        (d) => d.vendorId === WACOM_VID && d.productId === STU430_PID,
      ) || null
    )
  }

  /** Prompts the user to grant access to the pad (Chrome/Edge picker). */
  async requestAndConnect(): Promise<boolean> {
    if (!isWebHidSupported()) throw new Error('WebHID is not supported in this browser. Use Chrome or Edge.')
    const devs = await navigator.hid!.requestDevice({
      filters: [{ vendorId: WACOM_VID, productId: STU430_PID }],
    })
    if (!devs.length) return false
    return this.open(devs[0])
  }

  /** Connects to a device that was already granted. */
  async reconnect(): Promise<boolean> {
    const dev = await this.findAlreadyGranted()
    if (!dev) return false
    return this.open(dev)
  }

  private async open(dev: HIDDevice): Promise<boolean> {
    this.device = dev
    if (!dev.opened) await dev.open()
    dev.addEventListener('inputreport', this.inputHandler)

    // A freshly-plugged device sometimes takes a moment before it answers
    // feature reports. We bound every read with a hard timeout so the
    // 'connect' emit never hangs the connecting toast.
    const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T | null> =>
      Promise.race([
        p.catch(() => null as T | null),
        new Promise<null>((r) => setTimeout(() => r(null), ms)),
      ])

    const cap = await withTimeout(dev.receiveFeatureReport(CMD.capability), 700)
    if (cap && cap.byteLength >= 12) {
      this.capability = {
        tabletWidth: cap.getUint16(1),
        tabletHeight: cap.getUint16(3),
        pressureFactor: cap.getUint16(5),
        lcdWidth: cap.getUint16(7),
        lcdHeight: cap.getUint16(9),
        refreshRate: cap.getUint8(11),
      }
    }
    const info = await withTimeout(dev.receiveFeatureReport(CMD.information), 700)
    if (info && info.byteLength >= 12) {
      let name = ''
      for (let i = 1; i < 8; i++) {
        const c = info.getUint8(i)
        if (!c) break
        name += String.fromCharCode(c)
      }
      const firmware = `${info.getUint8(8)}.${info.getUint8(9)}.${info.getUint8(10)}.${info.getUint8(11)}`
      let eSerial = ''
      const ser = await withTimeout(dev.receiveFeatureReport(CMD.eSerial), 500)
      if (ser) {
        for (let i = 1; i < ser.byteLength; i++) {
          const c = ser.getUint8(i)
          if (!c) break
          eSerial += String.fromCharCode(c)
        }
      }
      this.info = { deviceName: name, firmware, eSerial }
    }

    this.emitConn('connect')
    return true
  }

  async disconnect(): Promise<void> {
    if (!this.device) return
    try {
      this.device.removeEventListener('inputreport', this.inputHandler)
    } catch { /* non-fatal: pad rejected the feature/listener call */ }
    try {
      if (this.device.opened) await this.device.close()
    } catch { /* non-fatal: pad rejected the feature/listener call */ }
    this.device = null
    this.emitConn('disconnect')
  }

  isConnected(): boolean {
    return !!this.device && this.device.opened
  }

  // ----- Display commands (Phase 1: static image only) -----

  async clearScreen(): Promise<void> {
    if (!this.device) return
    await this.device.sendFeatureReport(CMD.clearScreen, new Uint8Array([0]))
  }

  async setBacklight(intensity: 0 | 1 | 2 | 3): Promise<void> {
    if (!this.device) return
    await this.device.sendFeatureReport(CMD.brightness, new Uint8Array([intensity, 0]))
  }

  /**
   * Enable / disable real-time inking on the pad's LCD. This is the firmware
   * feature that draws the pen stroke directly on the device screen so the
   * signer sees their ink as they write — Phase 2 will exercise this fully,
   * but exposing the toggle here lets callers turn it on opportunistically.
   */
  async setInking(enabled: boolean): Promise<void> {
    if (!this.device) return
    await this.device.sendFeatureReport(CMD.inkMode, new Uint8Array([enabled ? 1 : 0]))
  }

  // ----- Pen data -----

  onPen(fn: PenListener): () => void {
    this.penListeners.add(fn)
    return () => this.penListeners.delete(fn)
  }

  onConnection(fn: ConnectionListener): () => void {
    this.connListeners.add(fn)
    return () => this.connListeners.delete(fn)
  }

  /** Returns the largest tablet-unit coordinates seen so far (for scaling). */
  getObservedRange(): { x: number; y: number } {
    return { x: this.maxX, y: this.maxY }
  }

  /** Tablet-unit dimensions to scale by — capability if known, else adaptive. */
  getCoordDenominator(): { x: number; y: number } {
    const x = Math.max(this.maxX, this.capability?.tabletWidth ?? 0, 1)
    const y = Math.max(this.maxY, this.capability?.tabletHeight ?? 0, 1)
    return { x, y }
  }

  private onInputReport(e: HIDInputReportEvent): void {
    if (e.reportId !== CMD.penData && e.reportId !== CMD.penDataTiming) return
    const v = e.data
    if (v.byteLength < 6) return

    const xRaw = v.getUint16(2)
    const yRaw = v.getUint16(4)
    // Pressure is the low 12 bits of bytes 0-1; high nibble carries status bits.
    const pressureRaw = v.getUint16(0) & 0x0fff

    if (xRaw > 0 && xRaw < 65000) this.maxX = Math.max(this.maxX, xRaw)
    if (yRaw > 0 && yRaw < 65000) this.maxY = Math.max(this.maxY, yRaw)

    const denomP = this.capability?.pressureFactor || 1023
    const packet: PenPacket = {
      xRaw,
      yRaw,
      pressureRaw,
      pressure: Math.min(1, pressureRaw / denomP),
      // STU-430-specific: rely on pressure for contact, not the sw bit.
      drawing: pressureRaw > 0,
    }
    this.penListeners.forEach((fn) => {
      try {
        fn(packet)
      } catch { /* non-fatal: pad rejected the feature/listener call */ }
    })
  }

  private emitConn(state: 'connect' | 'disconnect' | 'connecting'): void {
    this.connListeners.forEach((fn) => {
      try {
        fn(state)
      } catch { /* non-fatal: pad rejected the feature/listener call */ }
    })
  }
}

/** Singleton for the app — only one STU-430 supported at a time. */
let _instance: WacomStu430 | null = null
export function getWacomStu430(): WacomStu430 {
  if (!_instance) _instance = new WacomStu430()
  return _instance
}
